import React, { useEffect, useState } from "react";
import { Tabs } from "antd";
import { toast } from "react-toastify";
import ContentLoader from "../../utils/ContentLoader";
import Select from "react-select";
import { ARTICLE_TYPE_LABELS, mapArticleTypeOptions } from "../../utils/articleTypeUtils";
import { customSelectStyles } from "../../utils/customizedStyles";
import { Editor } from "@tinymce/tinymce-react";
import { API } from "../../config/constants";

// Text area
const RichTextEditor = React.memo(({ value, onChange, id }) => {
    return (
        <Editor
            key={id}
            value={value || ""}
            init={{
                height: 400,
                menubar: false,
                branding: false,
                promotion: false,
                statusbar: false,

                plugins: "lists link image wordcount code autolink charmap",

                toolbar:
                    "undo redo | blocks | bold italic underline | " +
                    "alignleft aligncenter alignright alignjustify | " +
                    "bullist numlist | custom_image_upload | removeformat",

                setup: (editor) => {
                    editor.ui.registry.addButton("custom_image_upload", {
                        icon: "image",
                        tooltip: "Insert Image",

                        onAction: () => {
                            const input = document.createElement("input");

                            input.type = "file";
                            input.accept = "image/*";

                            input.onchange = function () {
                                const file = this.files[0];

                                if (!file) return;

                                const reader = new FileReader();

                                reader.onload = function () {
                                    const id = "blobid" + new Date().getTime();
                                    const blobCache = editor.editorUpload.blobCache;
                                    const base64 = reader.result.split(",")[1];
                                    const blobInfo = blobCache.create(
                                        id,
                                        file,
                                        base64
                                    );
                                    blobCache.add(blobInfo);
                                    editor.insertContent(`<img src="${blobInfo.blobUri()}" alt="${file.name}" style="max-width:100%;" />`);
                                };

                                reader.readAsDataURL(file);
                            };

                            input.click();
                        },
                    });
                },
                paste_data_images: true,
                content_style:
                    "body { font-family:'Poppins', 'Noto Sans Tamil', sans-serif; font-size:14px }",
            }}
            onEditorChange={onChange}
        />
    );
});

const statusOptions = [
    { value: "draft", label: "Draft" },
    { value: "published", label: "Published" },
    { value: "reject", label: "Reject" },
];


export default function ArticleForm({
    onSubmit,
    onCancel,
    initialData = {},
    articleTypeOptions = [],
}) {
    const isEdit = Boolean(initialData?.id);

    const [loading] = useState(false);
    const [errors, setErrors] = useState({});

    const [articleOptions, setArticleOptions] = useState([]);

    const [formData, setFormData] = useState({
        article_title: {
            tn: "",
            en: "",
        },

        article_content: {
            tn: "",
            en: "",
        },

        article_type_id: "",
        article_type_others: "",
        article_banner_img: null,
        status: "draft",
    });

    // console.log("formData=======>>>", formData)
    console.log("initialData=======>>>", initialData)

    // =========================
    // MAP OPTIONS
    // =========================

    useEffect(() => {
        if (!articleTypeOptions?.length) return;
        setArticleOptions(mapArticleTypeOptions(articleTypeOptions));
    }, [articleTypeOptions]);

    // =========================
    // PREFILL
    // =========================
    useEffect(() => {
        if (initialData?.id) {
            const isOtherType = !!initialData?.article_type_others;

            setFormData({
                article_title: {
                    tn: initialData?.article_title?.tn || "",
                    en: initialData?.article_title?.en || "",
                },

                article_content: {
                    tn: initialData?.article_content?.tn || "",
                    en: initialData?.article_content?.en || "",
                },

                article_type_id: initialData?.article_type_id || "",
                // article_type_id: isOtherType
                //     ? "others"
                //     : initialData?.article_type_id || "",

                article_type_others: initialData?.article_type_others || "",
                status: initialData?.status || "draft",
            });
        }
    }, [initialData]);

    const othersId = articleOptions.find(
        (item) => item.label === ARTICLE_TYPE_LABELS.others
    )?.value;

    // =========================
    // CHANGE HANDLER
    // =========================
    const handleLangFieldChange = (field, lang, value) => {
        setFormData((prev) => ({
            ...prev,
            [field]: {
                ...prev[field],
                [lang]: value,
            },
        }));

        setErrors((prev) => ({
            ...prev,
            [`${field}_${lang}`]: "",
        }));
    };

    const updateField = (field, value) => {
        setFormData((prev) => {
            let updated = { ...prev, [field]: value };

            if (field === "article_type_id" && value !== othersId) {
                updated.article_type_others = "";
            }

            return updated;
        });

        setErrors((prev) => ({
            ...prev,
            [field]: "",
        }));
    };

    // =========================
    // VALIDATION
    // =========================
    const validate = () => {
        const newErrors = {};

        const tamilFilled =
            formData.article_title.tn?.trim() &&
            formData.article_content.tn?.trim();

        const englishFilled =
            formData.article_title.en?.trim() &&
            formData.article_content.en?.trim();

        if (!tamilFilled && !englishFilled) {
            if (!formData.article_title.tn?.trim())
                newErrors.article_title_tn = true;

            if (!formData.article_content.tn?.trim())
                newErrors.article_content_tn = true;

            if (!formData.article_title.en?.trim())
                newErrors.article_title_en = true;

            if (!formData.article_content.en?.trim())
                newErrors.article_content_en = true;
        }

        if (!formData.article_type_id) {
            newErrors.article_type_id = true;
        }

        if (formData.article_type_id === othersId) {
            if (!formData.article_type_others?.trim()) {
                newErrors.article_type_others = true;
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // =========================
    // SUBMIT
    // =========================
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validate()) {
            toast.error("Please fill required fields");
            return;
        }

        const fd = new FormData();

        fd.append("article_title", JSON.stringify(formData.article_title));
        fd.append("article_content", JSON.stringify(formData.article_content));
        if (formData.article_banner_img) {
            fd.append(
                "article_banner_img",
                formData.article_banner_img
            );
        }
        fd.append("status", formData.status);

        // if (formData.article_type_id === "others") {
        //     fd.append("article_type_id", "");
        //     fd.append("article_type_others", formData.article_type_others);
        // } else {
        //     fd.append("article_type_id", formData.article_type_id);
        // }

        fd.append("article_type_id", formData.article_type_id);
        if (formData.article_type_id === othersId) {
            fd.append("article_type_others", formData.article_type_others);
        }

        await onSubmit(fd);
    };

    if (loading) return <ContentLoader />;


    const bannerImageUrl = formData.article_banner_img
        ? URL.createObjectURL(formData.article_banner_img)
        : initialData?.article_banner_img
            ? `${API.IMAGE_BASE_URL}/${initialData.article_banner_img}`
            : "";

    return (
        <div className="col-lg-12">
            <div className="card mt-3">
                <div className="card-header">
                    <h5 className="card-title">
                        {isEdit ? "Edit Article" : "Create Article"}
                    </h5>
                </div>

                <div className="card-body">
                    <form onSubmit={handleSubmit} className="row gy-3">

                        <div className="col-12">
                            <Tabs
                                defaultActiveKey="tn"
                                items={[

                                    // ================= TAMIL =================
                                    {
                                        key: "tn",
                                        label: "Tamil",
                                        children: (
                                            <div className="row gy-3">

                                                <div className="col-md-6">
                                                    <label className="form-label">
                                                        Article Title <span style={{ color: "red" }}>*</span>
                                                    </label>

                                                    <input
                                                        className={`form-control ${errors.article_title_tn ? "is-invalid" : ""}`}
                                                        placeholder="Enter Article Title"
                                                        value={formData.article_title.tn}
                                                        onChange={(e) =>
                                                            handleLangFieldChange(
                                                                "article_title",
                                                                "tn",
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                </div>

                                                <div className="col-md-12">
                                                    <label className="form-label">
                                                        Article Content <span style={{ color: "red" }}>*</span>
                                                    </label>

                                                    <RichTextEditor
                                                        id="tn-editor"
                                                        value={formData.article_content.tn}
                                                        onChange={(value) =>
                                                            handleLangFieldChange("article_content", "tn", value)
                                                        }
                                                    />
                                                </div>

                                            </div>
                                        ),
                                    },

                                    // ================= ENGLISH =================
                                    {
                                        key: "en",
                                        label: "English",
                                        children: (
                                            <div className="row gy-3">

                                                <div className="col-md-6">
                                                    <label className="form-label">
                                                        Article Title <span style={{ color: "red" }}>*</span>
                                                    </label>

                                                    <input
                                                        className={`form-control ${errors.article_title_en ? "is-invalid" : ""}`}
                                                        placeholder="Enter Article Title"
                                                        value={formData.article_title.en}
                                                        onChange={(e) =>
                                                            handleLangFieldChange(
                                                                "article_title",
                                                                "en",
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                </div>

                                                <div className="col-md-12">
                                                    <label className="form-label">
                                                        Article Content <span style={{ color: "red" }}>*</span>
                                                    </label>

                                                    <RichTextEditor
                                                        id="en-editor"
                                                        value={formData.article_content.en}
                                                        onChange={(value) =>
                                                            handleLangFieldChange("article_content", "en", value)
                                                        }
                                                    />
                                                </div>

                                            </div>
                                        ),
                                    },

                                    // ================= COMMON =================
                                    {
                                        key: "common",
                                        label: "Common",
                                        children: (
                                            <div className="row gy-3">

                                                {/* ARTICLE TYPE STYLE */}
                                                <div className="col-md-6">
                                                    <label className="form-label">
                                                        Article Type <span style={{ color: "red" }}>*</span>
                                                    </label>

                                                    <Select
                                                        options={articleOptions}
                                                        value={
                                                            articleOptions.find(
                                                                (option) =>
                                                                    option.value === formData.article_type_id
                                                            ) || null
                                                        }
                                                        placeholder="Select Article Type"
                                                        onChange={(selected) => {
                                                            if (!selected) {
                                                                updateField("article_type_id", "");
                                                                updateField("article_type_others", "");
                                                                return;
                                                            }

                                                            updateField("article_type_id", selected.value);

                                                            if (selected.value !== othersId) {
                                                                updateField("article_type_others", "");
                                                            }
                                                        }}
                                                        styles={customSelectStyles()}
                                                    />

                                                    {errors.article_type_id && (
                                                        <span className="text-danger">
                                                            Required
                                                        </span>
                                                    )}
                                                </div>

                                                {/* OTHERS FIELD */}
                                                {formData.article_type_id === othersId && (
                                                    <div className="col-md-6">
                                                        <label className="form-label">
                                                            Other Article Type
                                                        </label>

                                                        <input
                                                            className="form-control"
                                                            placeholder="Enter Other Article Type"
                                                            value={formData.article_type_others}
                                                            onChange={(e) =>
                                                                updateField(
                                                                    "article_type_others",
                                                                    e.target.value
                                                                )
                                                            }
                                                        />

                                                        {errors.article_type_others && (
                                                            <span className="text-danger">
                                                                Required
                                                            </span>
                                                        )}
                                                    </div>
                                                )}

                                                {/* Status */}
                                                <div className="col-md-6">
                                                    <label className="form-label">Status</label>

                                                    <Select
                                                        options={statusOptions}
                                                        value={statusOptions.find(
                                                            (option) => option.value === formData.status
                                                        )}
                                                        onChange={(selected) =>
                                                            updateField("status", selected.value)
                                                        }
                                                        styles={customSelectStyles()}
                                                    />
                                                </div>

                                                <div className="col-md-6">
                                                    <label className="form-label">
                                                        Banner Image <span className="text-danger">*</span>
                                                    </label>

                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        className={`form-control ${errors.article_banner_img ? "is-invalid" : ""
                                                            }`}
                                                        onChange={(e) => {
                                                            const file = e.target.files?.[0];

                                                            if (!file) return;

                                                            setFormData((prev) => ({
                                                                ...prev,
                                                                article_banner_img: file
                                                            }));

                                                            setErrors((prev) => ({
                                                                ...prev,
                                                                article_banner_img: "",
                                                            }));
                                                        }}
                                                    />

                                                    {bannerImageUrl && (
                                                        <div className="mb-2 mt-2">
                                                            <img
                                                                src={bannerImageUrl}
                                                                alt="Banner Preview"
                                                                style={{
                                                                    width: "120px",
                                                                    height: "80px",
                                                                    objectFit: "cover",
                                                                    borderRadius: "4px",
                                                                    border: "1px solid #ddd",
                                                                }}
                                                            />
                                                        </div>
                                                    )}
                                                </div>

                                            </div>
                                        ),
                                    }
                                ]}
                            />
                        </div>

                        {/* BUTTONS */}
                        <div className="col-12 d-flex justify-content-end gap-2">
                            <button type="button" className="btn btn-secondary" onClick={onCancel}>
                                Cancel
                            </button>

                            <button type="submit" className="btn btn-primary">
                                {isEdit ? "Update Article" : "Create Article"}
                            </button>
                        </div>

                    </form>
                </div>
            </div>
        </div>
    );
}